import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ArticleRepository } from '@/infrastructure/repositories/article.repository'
import { createApiClient } from '@/infrastructure/api/client'
import type { Article } from '@/domain/entities/article.entity'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

/**
 * Store для страницы деталей статьи (только чтение).
 */
export const useArticleDetailsStore = defineStore('article-details', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('article-details')
  const article = ref<Article | null>(null)
  const loading = ref(false)

  const hasArticle = computed(() => article.value != null)

  const apiClient = createApiClient({
    baseUrl: useRuntimeConfig().public.NUXT_APP_LANDING_API,
  })
  const articleRepository = new ArticleRepository(apiClient)

  async function fetchArticleBySlug(slug: string): Promise<Article | null> {
    const isRefetchSameSlug = article.value?.article_slug === slug
    if (!isRefetchSameSlug) {
      article.value = null
    }
    clearErrors()
    loading.value = true
    try {
      const result = await articleRepository.findBySlug(slug)
      article.value = result
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      return null
    } finally {
      loading.value = false
    }
  }

  function applyArticleViews(articleId: string, views: number | null) {
    if (article.value?.id !== articleId || views === null) return
    article.value = {
      ...article.value,
      article_views: views,
    }
  }

  async function incrementArticleView(articleId: string): Promise<number | null> {
    if (!articleId) return article.value?.article_views ?? null
    const views = await articleRepository.incrementView(articleId)
    applyArticleViews(articleId, views)
    return views
  }

  function reset() {
    article.value = null
    loading.value = false
    clearErrors()
  }

  return {
    article,
    loading,
    error,
    hasArticle,
    hasError,
    fetchArticleBySlug,
    incrementArticleView,
    reset,
  }
})
